import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessagesSquare, Send, ExternalLink, Loader2 } from "lucide-react";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Discussions({ dkey, placeName }) {
  const { tr } = useI18n();
  const { user, signIn } = useAuth();
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/discussions/${encodeURIComponent(dkey)}`)
      .then((r) => setMessages(r.data))
      .catch(() => setMessages([]));
  }, [dkey]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/discussions/${encodeURIComponent(dkey)}`, { text: t },
        { headers: { Authorization: `Bearer ${localStorage.getItem("sp_token")}` } });
      setMessages((m) => [...(m || []), r.data]);
      setText("");
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
    } catch {
      toast.error(tr("Could not post — please sign in again."));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="community" className="scroll-mt-32 mb-12" data-testid="discussions-section">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-xl bg-[#F5F3FF] flex items-center justify-center"><MessagesSquare size={17} className="text-[#7C3AED]" /></span>
        <h2 className="text-xl font-extrabold text-[#0F2557] flex-1">{tr("Community Discussion")}</h2>
        <a href={`https://chats.socialpulse.com/${dkey}`} target="_blank" rel="noreferrer" data-testid="discussions-chats-link"
          className="text-xs font-extrabold text-[#7C3AED] inline-flex items-center gap-1.5 hover:underline">
          Chats.SocialPulse.com <ExternalLink size={11} />
        </a>
      </div>
      <div className="mt-4 border border-[#D8CCF2] rounded-3xl bg-[#FCFAFF] overflow-hidden">
        <div className="max-h-80 overflow-y-auto p-5 space-y-4" data-testid="discussions-list">
          {messages === null && <div className="flex items-center gap-2 text-[#7C3AED] text-sm font-bold"><Loader2 size={14} className="animate-spin" /> {tr("Loading discussion…")}</div>}
          {messages?.length === 0 && (
            <p className="text-sm text-[#64748B]">{tr("No messages yet — start the conversation about")} {placeName}.</p>
          )}
          {messages?.map((m) => (
            <div key={m.id} data-testid="discussion-message" className="bg-white border border-[#EDE7F8] rounded-2xl px-4 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-xs font-extrabold text-[#6D28D9]">{m.author}</span>
                <span className="text-[0.6rem] font-semibold text-[#94A3B8]">{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm text-[#334155] leading-relaxed mt-1">{m.text}</p>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="border-t border-[#EDE7F8] p-3 bg-white">
          {user ? (
            <div className="flex gap-2">
              <input
                data-testid="discussion-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                maxLength={2000}
                placeholder={`${tr("Share something about")} ${placeName}…`}
                className="flex-1 text-sm px-4 py-2.5 rounded-xl border border-[#E2E8F0] focus:outline-none focus:border-[#7C3AED] text-[#0F2557] placeholder:text-[#94A3B8] min-w-0"
              />
              <button data-testid="discussion-send-button" onClick={send} disabled={sending || !text.trim()}
                className="bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-40 text-white font-bold text-sm px-5 rounded-xl flex items-center gap-2 transition-colors">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {tr("Post")}
              </button>
            </div>
          ) : (
            <button data-testid="discussion-signin-button" onClick={signIn}
              className="w-full text-sm font-bold text-[#7C3AED] bg-[#F5F3FF] hover:bg-[#EDE7F8] rounded-xl py-3 transition-colors">
              {tr("Sign in with Google to join the discussion")}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
